import { useState } from 'react'

import { useCreateFilter } from '../../hooks/useCreateFilter'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import type { ApolloError } from '@apollo/client'

import type { ColorScheme } from '@hivewatch/dark'
import { Modal, TextInput } from '@hivewatch/dark'

import { MODAL_Z_INDEX } from '@/styles/theme'

import { IncidentFiltersFormInputsI } from '../../types/form'

const NAME_MAX_LENGTH = 255

interface DarkCreateFilterModalIProps {
  isOpen: boolean
  onClose: () => void
  incidentFiltersFormValues: IncidentFiltersFormInputsI
  colorScheme: ColorScheme
}

export const DarkCreateFilterModal = ({
  isOpen,
  onClose,
  incidentFiltersFormValues,
  colorScheme,
}: DarkCreateFilterModalIProps) => {
  const { t } = useTranslation()
  const { showSuccess, showError } = useToasts()
  const { create, isCreateLoading } = useCreateFilter()

  const [name, setName] = useState('')
  const [hasNameError, setHasNameError] = useState(false)

  const handleSave = async () => {
    if (name.trim().length === 0) {
      setHasNameError(true)
      return
    }
    try {
      await create({ name }, incidentFiltersFormValues)
      showSuccess(t('incidents:filter_create_success'))
      onClose()
    } catch (error) {
      showError(error as ApolloError)
    }
  }

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveLoading={isCreateLoading}
      onClose={onClose}
      onSave={handleSave}
      saveButtonLabel={t('incidents:filter_create_title')}
      showCancelButton
      size='compact'
      title={t('incidents:filter_create_title')}
      zIndex={MODAL_Z_INDEX}
    >
      <TextInput
        autoFocus
        colorScheme={colorScheme}
        hasWarning={hasNameError}
        label={t('incidents:filter_name_label')}
        maxLength={NAME_MAX_LENGTH}
        onValueChange={(value) => {
          setName(value)
          if (hasNameError && value.trim().length > 0) setHasNameError(false)
        }}
        placeholder={t('incidents:filter_name_placeholder')}
        value={name}
        warningText={t('incidents:filter_name_required')}
        width='100%'
      />
    </Modal>
  )
}
